import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

export default function FlashcardDeck({ certId }) {
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showUnknownOnly, setShowUnknownOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/certifications/${certId}/flashcards`).then((r) => setCards(r.data)).finally(() => setLoading(false));
  }, [certId]);

  const visibleCards = showUnknownOnly ? cards.filter((c) => !c.known) : cards;
  const card = visibleCards[Math.min(index, Math.max(visibleCards.length - 1, 0))];

  useEffect(() => {
    if (index > visibleCards.length - 1 && visibleCards.length > 0) setIndex(visibleCards.length - 1);
  }, [visibleCards.length, index]);

  const mark = async (known) => {
    if (!card) return;
    await api.post(`/certifications/${certId}/flashcards/${card.id}/mark`, { known });
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, known } : c)));
    setFlipped(false);
    // When filtering to unknown-only, a card marked "known" is removed from the
    // list in place — the index already points at the next card, so don't advance.
    if (!(showUnknownOnly && known)) {
      setIndex((i) => Math.min(i + 1, visibleCards.length - 1));
    }
  };

  const next = () => { setFlipped(false); setIndex((i) => Math.min(i + 1, visibleCards.length - 1)); };
  const prev = () => { setFlipped(false); setIndex((i) => Math.max(i - 1, 0)); };

  if (loading) return <div className="flex justify-center py-16" data-testid="flashcards-loading"><div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  const knownCount = cards.filter((c) => c.known).length;

  return (
    <div className="max-w-xl mx-auto" data-testid="flashcard-deck">
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm text-slate-400" data-testid="flashcard-known-count">{knownCount}/{cards.length} known</span>
        <button
          onClick={() => { setShowUnknownOnly((v) => !v); setIndex(0); setFlipped(false); }}
          data-testid="flashcard-toggle-unknown-filter"
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${showUnknownOnly ? "bg-indigo-400/15 border-indigo-400/40 text-indigo-300" : "border-white/10 text-slate-400 hover:text-white"}`}
        >
          Review unknown only
        </button>
      </div>

      {!card ? (
        <div className="glass-panel rounded-2xl p-10 text-center text-slate-400" data-testid="flashcard-deck-empty">
          {showUnknownOnly ? "You've marked every card as known! 🎉" : "No flashcards found."}
        </div>
      ) : (
        <>
          <div className="relative h-64 [perspective:1200px]" data-testid="flashcard-card">
            <motion.div
              onClick={() => setFlipped((f) => !f)}
              className="absolute inset-0 cursor-pointer [transform-style:preserve-3d]"
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 0.45 }}
            >
              <div className="absolute inset-0 glass-panel rounded-2xl p-8 flex flex-col items-center justify-center text-center [backface-visibility:hidden]">
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 mb-3">{card.topic}</span>
                <p className="text-lg font-medium text-white">{card.front}</p>
                <span className="text-xs text-slate-500 mt-4">Click to flip</span>
              </div>
              <div className="absolute inset-0 glass-panel rounded-2xl p-8 flex flex-col items-center justify-center text-center [backface-visibility:hidden] [transform:rotateY(180deg)]">
                <p className="text-base text-slate-200 leading-relaxed">{card.back}</p>
              </div>
            </motion.div>
          </div>

          <div className="flex items-center justify-between mt-5">
            <button onClick={prev} disabled={index === 0} data-testid="flashcard-prev-btn" className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <AnimatePresence mode="wait">
              {flipped ? (
                <motion.div key="know-buttons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3">
                  <button onClick={() => mark(false)} data-testid="flashcard-mark-unknown-btn" className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition-colors">
                    <X className="w-4 h-4" /> Don't know
                  </button>
                  <button onClick={() => mark(true)} data-testid="flashcard-mark-known-btn" className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
                    <Check className="w-4 h-4" /> Know it
                  </button>
                </motion.div>
              ) : (
                <motion.span key="flip-hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-slate-500">{index + 1} / {visibleCards.length}</motion.span>
              )}
            </AnimatePresence>
            <button onClick={next} disabled={index === visibleCards.length - 1} data-testid="flashcard-next-btn" className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => { setIndex(0); setFlipped(false); }} data-testid="flashcard-restart-btn" className="mt-4 flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mx-auto">
            <RotateCcw className="w-3.5 h-3.5" /> Restart deck
          </button>
        </>
      )}
    </div>
  );
}
